"use client"
import { type FragmentOf, readFragment } from "@opensea/graphql"
import { usePersona } from "@opensea/navigation"
import { isAddressEqual } from "@opensea/utils"
import { useCancelOrderFlow } from "@opensea/wallet"
import { useTranslations } from "next-intl"
import { CRYPTO_PUNKS_ADDRESS } from "@/constants/constants"
import { useCancelItemsListingsFragment } from "./use-cancel-items-listings.fragment"

export const useCancelItemsListings = (
  itemKeys: (
    | (FragmentOf<typeof useCancelItemsListingsFragment> & {
        quantity?: number
      })
    | undefined
  )[],
) => {
  const t = useTranslations("useCancelItemsListings")
  const items = itemKeys.map(itemKey => {
    return readFragment(useCancelItemsListingsFragment, itemKey)
  })
  const { persona } = usePersona()
  const mode = persona === "advanced" ? "minimized" : "full"
  const { cancelItemsListings: walletCancelItemsListings } = useCancelOrderFlow(
    { mode },
  )

  const getDisabledReason = () => {
    // no support for cryptopunk
    if (
      items.some(item =>
        isAddressEqual(item?.contractAddress, CRYPTO_PUNKS_ADDRESS),
      )
    ) {
      return t("errors.cryptoPunksNotSupported")
    }
    // If not all item chains are the same
    if (
      items.some(item => item?.chain.identifier !== items[0]?.chain.identifier)
    ) {
      return t("errors.differentChains")
    }

    return undefined
  }

  const filteredItems = items.flatMap(item =>
    // Filter out items where there exists a listing by the owner
    item?.lowestListingForOwner
      ? [
          {
            chain: { identifier: item.chain.identifier },
            contractAddress: item.contractAddress,
            tokenId: item.tokenId,
            quantity: 1,
          },
        ]
      : [],
  )

  const disabledReason = getDisabledReason()
  const disabled = Boolean(disabledReason) || filteredItems.length === 0

  function cancelItemsListings() {
    if (filteredItems.length) {
      // Items can technically contain undefined elements because of pagination, so we filter them out
      walletCancelItemsListings(filteredItems)
    }
  }

  return {
    disabled,
    disabledReason,
    cancelItemsListings,
  }
}
